/* eslint-disable jsx-a11y/anchor-is-valid */
import {Field, Form, Formik, FormikProps} from 'formik'
import React, {useEffect} from 'react'
import {useIntl} from 'react-intl'
import * as Yup from 'yup'
import {ButtonPrimary} from '../../form/Buttons/ButtonPrimary/ButtonPrimary'
import CustomInput from '../../form/CustomInput/CustomInput'
import Radio from '../../form/CustomRadio/Radio'
import RadioGroup from '../../form/CustomRadio/RadioGroup'
import {ICategory} from '../ModalModel'
import {ModalWrapper} from '../ModalWrapper/ModalWrapper'
import makeAnimated from 'react-select/animated'
import {DropdownIndicatorProps, components} from 'react-select'
import CustomSelect from '../../form/CustomSelect/CustomSelect'
import CustomTagInput from '../../form/CustomTagInput/CustomTagInput'
import {useLang} from '../../../../i18n/MetaCrewi18n'

type Option = {
  value: string
  label: string
}

type Props = {
  show: boolean
  isLoading?: boolean
  isShowButton?: boolean
  initialValuesDefault?: any
  listOptionLanguage?: Option[]
  handleClose: () => void
  handleSubmit?: (values: ICategory, id: string | undefined) => void
  data?: any
}

function convertToDictionary(dynamicValues: any) {
  const dictionary: any = {}

  for (const key in dynamicValues) {
    const value = dynamicValues[key]
    dictionary[key] = {name: value}
  }

  return dictionary
}

function convertToObject(arr: any) {
  return arr?.reduce(
    (result: any, item: any) => {
      result['translations'][item?.value] = ''
      return result
    },
    {translations: {}}
  )
}

function createValidationSchema(initialValuesDefault: any, intl: any) {
  const schema: any = {}
  const {translations = {}} = initialValuesDefault
  for (const language in translations) {
    if (translations.hasOwnProperty(language)) {
      schema[language] = Yup.object().shape({
        name: Yup.string().required('언어에서 이름은 필수입니다.'),
      })
    }
  }

  return Yup.object().shape({
    translations: Yup.object().shape(schema),
  })
}

const DropdownIndicator = (props: DropdownIndicatorProps<Option, true>) => {
  return (
    <components.DropdownIndicator {...props}>
      <svg width='12' height='9' viewBox='0 0 12 9' fill='none' xmlns='http://www.w3.org/2000/svg'>
        <path d='M6 9L0.803849 -9.78799e-07L11.1962 -7.02746e-08L6 9Z' fill='#111111' />
      </svg>
    </components.DropdownIndicator>
  )
}
const CategoryFormModal: React.FC<Props> = ({
  show = true,
  isLoading = false,
  isShowButton = false,
  listOptionLanguage = [],
  initialValuesDefault,
  handleClose,
  handleSubmit,
  data = null,
}) => {
  const intl = useIntl()
  const lang = useLang()
  const OPTIONS_CATEGORY_TYPE: Option[] = [
    {
      value: 'default',
      label: intl.formatMessage({
        id: 'DEFAULT',
      }),
    },
    {
      value: 'custom',
      label: intl.formatMessage({
        id: 'CUSTOM',
      }),
    },
  ]

  const optionCategories = data?.map((item: any) => ({
    value: item?.id,
    label: intl.formatMessage({
      id: item?.name,
    }),
  }))

  const [categoryType, setCategoryType] = React.useState(initialValuesDefault?.type || 'default')
  const [categoryIds, setCategoryIds] = React.useState(initialValuesDefault?.category_ids || [])
  const animatedComponents = makeAnimated()
  const languages = listOptionLanguage
  const translations = initialValuesDefault?.translations
    ? convertToDictionary(initialValuesDefault?.translations)
    : {}
  const keywords: any = (initialValuesDefault?.keywords || [])?.map((item: any) => ({
    id: item,
    text: item,
  }))
  const validationSchema = initialValuesDefault?.translations
    ? createValidationSchema(initialValuesDefault, intl)
    : createValidationSchema(convertToObject(languages), intl)
  const initialValues: any = {
    // name: initialValuesDefault?.name || '',
    translations: translations,
    status: initialValuesDefault?.id ? String(initialValuesDefault?.status === 1) : 'true',
    type: initialValuesDefault?.type || 'default',
    keywords: keywords,
    category_ids: optionCategories?.filter((item: any) =>
      initialValuesDefault?.filter_category?.find((id: any) => id === item?.value)
    ),
  }
  const onSubmit = (values: any) => {
    handleSubmit && handleSubmit(values, initialValuesDefault?.id)
  }

  useEffect(() => {
    setCategoryType(initialValuesDefault?.type || 'default')
  }, [initialValuesDefault])

  return (
    <ModalWrapper show={show}>
      <div className='container-xxl'>
        <div className='modal-body pt-0'>
          <div className='modal-header row'>
            <div className='col-10 col-lg-10 col-md-10'>
              <h1 className='mt-2'>
                {intl.formatMessage({
                  id: initialValuesDefault?.id ? 'TITLE_UPDATE_CATEGORY' : 'TITLE_ADD_CATEGORY',
                })}
                {initialValuesDefault?.name && (
                  <span className='fs-8 text-primary ms-2'>{`( ${
                    lang === 'ko'
                      ? initialValuesDefault?.translations?.ko
                      : initialValuesDefault?.translations?.en
                  } )`}</span>
                )}
              </h1>
            </div>
            <div className='col-2 col-lg-2 col-md-2 text-end'>
              {/* begin::Close */}
              <div
                className='btn btn-icon btn-sm ms-2'
                onClick={() => {
                  setCategoryType(null)
                  handleClose()
                }}
              >
                <svg
                  width='30'
                  height='30'
                  viewBox='0 0 30 30'
                  fill='none'
                  xmlns='http://www.w3.org/2000/svg'
                >
                  <path
                    d='M22.5 7.5L7.5 22.5M7.5 7.5L22.5 22.5'
                    stroke='black'
                    strokeWidth='2'
                    strokeLinecap='round'
                    strokeLinejoin='round'
                  />
                </svg>
              </div>
              {/* end::Close */}
            </div>
          </div>
          {/* begin::Search */}
          <Formik
            initialValues={initialValues}
            validationSchema={validationSchema}
            onSubmit={onSubmit}
          >
            {({touched, errors, values}: FormikProps<ICategory>) => (
              <Form className='p-6'>
                {languages?.map((item: any) => (
                  <div className='row mb-9' key={item?.value}>
                    <Field
                      label={
                        item?.label ? intl.formatMessage({id: item?.label.toUpperCase()}) : '' || ''
                      }
                      name={`translations.${item?.value}.name`}
                      type='text'
                      placeholder={
                        item?.label ? intl.formatMessage({id: item?.label.toUpperCase()}) : ''
                      }
                      autoComplete='off'
                      className='form-control form-control-solid'
                      component={CustomInput}
                    />
                  </div>
                ))}
                <div className='row mb-7'>
                  <Field
                    label={intl.formatMessage({id: 'COL_CATEGORY_TYPE_TITLE'})}
                    placeholder={intl.formatMessage({id: 'COL_CATEGORY_TYPE_TITLE'})}
                    name='type'
                    className='react-select-container'
                    classNamePrefix='react-select'
                    closeMenuOnSelect={true}
                    components={{...animatedComponents, DropdownIndicator}}
                    value={initialValues.type}
                    options={OPTIONS_CATEGORY_TYPE}
                    component={CustomSelect}
                    isMulti={false}
                    isObjOption
                    handleOnChange={(value: any) => {
                      setCategoryType((prev: any) => value.value)
                    }}
                  />
                </div>
                {categoryType === 'custom' && (
                  <>
                    <div className='row mb-9'>
                      <Field
                        placeholder={intl.formatMessage({id: 'COL_CATEGORY_IDS_TITLE'})}
                        name='category_ids'
                        className='react-select-container'
                        classNamePrefix='react-select'
                        closeMenuOnSelect={true}
                        components={{...animatedComponents, DropdownIndicator}}
                        options={optionCategories}
                        component={CustomSelect}
                        isMulti={true}
                        isObjOption
                        handleOnChange={(value: any) => {
                          setCategoryIds((prev: any) => value.value)
                        }}
                      />
                    </div>
                    <div className='row mb-9'>
                      <Field
                        required={false}
                        label={''}
                        name='keywords'
                        type='text'
                        placeholder={intl.formatMessage({id: 'FORM_KEYWORDS'})}
                        autoComplete='off'
                        className='form-control form-control-solid'
                        component={CustomTagInput}
                      />
                    </div>
                  </>
                )}
                <div className='row mb-7'>
                  <RadioGroup
                    id='status'
                    label={intl.formatMessage({id: 'FORM_STATUS'})}
                    value={values.status}
                    error={errors.status}
                    touched={touched.status}
                  >
                    <Field
                      component={Radio}
                      name='status'
                      id='status0'
                      valueSet='false'
                      label={intl.formatMessage({id: 'FORM_DEACTIVATION'})}
                    />
                    <Field
                      component={Radio}
                      name='status'
                      id='status1'
                      valueSet='true'
                      label={intl.formatMessage({id: 'FORM_ACTIVATION'})}
                    />
                  </RadioGroup>
                </div>
                <div>
                  <div className='text-end'>
                    {isShowButton && (
                      <ButtonPrimary
                        text={intl.formatMessage({
                          id: initialValuesDefault?.id ? 'BUTTON_UPDATE' : 'BUTTON_CREATE',
                        })}
                        type='submit'
                        isLoading={isLoading}
                      />
                    )}
                  </div>
                </div>
              </Form>
            )}
          </Formik>
        </div>
      </div>
    </ModalWrapper>
  )
}

export {CategoryFormModal}
